export function LandingMechanism() {
  return (
    <section id="mechanism" className="border-y-2 border-black bg-zinc-50 py-24 select-none relative overflow-hidden font-mono">
      <div className="max-w-4xl mx-auto px-6 flex flex-col items-center">
        {/* HEADER */}
        <p className="text-xs mb-4 uppercase tracking-wider font-bold text-gray-500">THE CORE BRAND METAPHOR</p>
        <h2 className="text-4xl font-black uppercase tracking-tight leading-none text-center">
          All Your Tools. Under One Wing.
        </h2>
        <p className="text-gray-700 mt-4 max-w-3xl text-center font-sans text-sm font-bold leading-relaxed">
          Right now your alerts are scattered. Sentry says one thing. Datadog says another. GitHub says nothing. You’re the one connecting the dots at 3am. We bring Sentry, GitHub, Datadog, and your URLs under one wing. When anything breaks, we’ve already checked everything. You just get the fix.
        </p>

        {/* BRUTALIST WIRE SWITCHBOARD DIAGRAM */}
        <div className="relative mt-20 w-full max-w-3xl flex flex-col items-center">
          
          {/* TOP ROW: TRIGGERS IN (PagerDuty, Datadog, GitHub, Vercel, Grafana) */}
          <div className="flex justify-between w-full relative z-10 px-2">
            {/* PagerDuty */}
            <div className="flex flex-col items-center w-20">
              <div className="w-14 h-14 border-2 border-black bg-white flex items-center justify-center shadow-[4px_4px_0_0_#000]">
                <img src="/integrations/pagerduty.png" alt="PagerDuty" className="w-8 h-8 object-contain" />
              </div>
              <span className="text-[9px] font-black uppercase mt-2 text-zinc-500">PAGERDUTY</span>
            </div>

            {/* Datadog */}
            <div className="flex flex-col items-center w-20">
              <div className="w-14 h-14 border-2 border-black bg-white flex items-center justify-center shadow-[4px_4px_0_0_#000]">
                <img src="/integrations/datadog.png" alt="Datadog" className="w-8 h-8 object-contain" />
              </div>
              <span className="text-[9px] font-black uppercase mt-2 text-zinc-500">DATADOG</span>
            </div>

            {/* GitHub */}
            <div className="flex flex-col items-center w-20">
              <div className="w-14 h-14 border-2 border-black bg-white flex items-center justify-center shadow-[4px_4px_0_0_#000]">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.464-1.11-1.464-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.831.092-.646.35-1.086.636-1.336-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.579.688.481C19.137 20.162 22 16.418 22 12c0-5.523-4.477-10-10-10z" />
                </svg>
              </div>
              <span className="text-[9px] font-black uppercase mt-2 text-zinc-500">GITHUB</span>
            </div>

            {/* Vercel */}
            <div className="flex flex-col items-center w-20">
              <div className="w-14 h-14 border-2 border-black bg-white flex items-center justify-center shadow-[4px_4px_0_0_#000]">
                <svg className="w-8 h-8 text-black fill-current" viewBox="0 0 24 24">
                  <path d="M12 1L24 22H0L12 1Z" />
                </svg>
              </div>
              <span className="text-[9px] font-black uppercase mt-2 text-zinc-500">VERCEL</span>
            </div>

            {/* Grafana */}
            <div className="flex flex-col items-center w-20">
              <div className="w-14 h-14 border-2 border-black bg-white flex items-center justify-center shadow-[4px_4px_0_0_#000]">
                <img src="/integrations/grafana.png" alt="Grafana" className="w-8 h-8 object-contain" />
              </div>
              <span className="text-[9px] font-black uppercase mt-2 text-zinc-500">GRAFANA</span>
            </div>

            {/* More Triggers */}
            <div className="flex flex-col items-center w-20">
              <div className="w-14 h-14 border-2 border-dashed border-zinc-400 bg-white flex items-center justify-center shadow-[4px_4px_0_0_#ccc]">
                <span className="text-[10px] font-black text-zinc-400 uppercase">+ MORE</span>
              </div>
              <span className="text-[9px] font-black uppercase mt-2 text-zinc-400">AND MORE</span>
            </div>
          </div>

          {/* TOP ROUTING LINES: Clean straight drop lines routing to the common center horizontal connector bus */}
          <div className="hidden md:block w-full h-20 relative pointer-events-none z-0">
            <svg className="absolute inset-0 w-full h-full" fill="none">
              {/* Converge points layout (using percentage coordinates based on the 3xl wrapper box grid columns) */}
              {/* Drop straight down, then map cleanly to the center trigger entry point */}
              <path d="M 40 0 L 40 45 L 380 45 L 380 80" stroke="black" strokeWidth="2" strokeDasharray="4,4" />
              <path d="M 170 0 L 170 45 L 380 45 L 380 80" stroke="black" strokeWidth="2" strokeDasharray="4,4" />
              <path d="M 300 0 L 300 80" stroke="black" strokeWidth="2" strokeDasharray="4,4" />
              <path d="M 430 0 L 430 45 L 380 45 L 380 80" stroke="black" strokeWidth="2" strokeDasharray="4,4" />
              <path d="M 560 0 L 560 45 L 380 45 L 380 80" stroke="black" strokeWidth="2" strokeDasharray="4,4" />
              <path d="M 690 0 L 690 45 L 380 45 L 380 80" stroke="black" strokeWidth="2" strokeDasharray="4,4" />
            </svg>
          </div>

          {/* MIDDLE: RAW INCIDENT ALERT METADATA BOX */}
          <div className="my-4 md:my-0 relative z-10 w-full max-w-xl">
            <div className="border-2 border-black bg-white py-2.5 px-6 text-center shadow-[4px_4px_0_0_#000] font-mono text-xs font-black uppercase tracking-wider">
              RAW INCIDENT: api.yoursaas.com 503 Outage
            </div>
          </div>

          {/* CONNECTOR LINE (BRAND BOX TO MAIN LOGO) */}
          <div className="hidden md:block w-[2px] h-8 bg-black border-l-2" />

          {/* CENTER: UPTIMEWING LOGO DISPLAY */}
          <div className="relative z-10 flex flex-col items-center">
            <div className="w-20 h-20 border-2 border-black bg-white flex flex-col items-center justify-center shadow-[4px_4px_0_0_#000]">
              <img src="/logo.png" alt="UptimeWing" className="w-12 h-12 object-contain" />
            </div>
            <span className="text-[10px] font-black uppercase font-mono mt-2 text-zinc-500">UPTIMEWING</span>
          </div>

          {/* CONNECTOR LINE (MAIN LOGO TO EXPANDED INCIDENT CARD) */}
          <div className="hidden md:block w-[2px] h-8 bg-black border-l-2" />

          {/* EXPANDED INCIDENT EXPLANATION CARD CONTAINER */}
          <div className="relative z-10 w-full max-w-lg border-2 border-black bg-white p-5 text-left font-mono shadow-[4px_4px_0_0_#000]">
            <div className="flex items-center gap-2 border-b border-black pb-2 mb-3">
              <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-[9px] font-black uppercase text-red-500">UptimeWing Diagnostic Engine</span>
            </div>
            <p className="text-xs font-black uppercase text-black leading-tight">Root Cause: Broken deployment #882bcf</p>
            <div className="mt-2 space-y-1.5 text-[10px] text-zinc-600 leading-relaxed font-bold">
              <p>• Telemetry caught Datadog CPU spike matching PagerDuty alert.</p>
              <p>• Correlated Vercel production deployment trigger dispatched 45s prior.</p>
              <p>• Remediation recommendation: Auto-Rollback to last stable build (#882bce).</p>
            </div>
            <div className="border-t border-black/10 mt-3 pt-3 flex justify-between items-center">
              <span className="text-[9px] font-black text-zinc-400 uppercase">TTR: 12.2s</span>
              <span className="text-[9px] font-black text-green-600 bg-green-50 border border-green-200 px-1.5 py-0.5 uppercase">VERIFIED</span>
            </div>
          </div>

          {/* BOTTOM ROUTING LINES: Clean straight drop lines routing to all outputs */}
          <div className="hidden md:block w-full h-20 relative pointer-events-none z-0">
            <svg className="absolute inset-0 w-full h-full" fill="none">
              {/* Single bus split layout connecting the bottom of the card (X=380) down to the 7 targets */}
              <path d="M 380 0 L 380 35 L 50 35 L 50 80" stroke="black" strokeWidth="2" strokeDasharray="4,4" />
              <path d="M 380 0 L 380 35 L 160 35 L 160 80" stroke="black" strokeWidth="2" strokeDasharray="4,4" />
              <path d="M 380 0 L 380 35 L 270 35 L 270 80" stroke="black" strokeWidth="2" strokeDasharray="4,4" />
              <path d="M 380 0 L 380 80" stroke="black" strokeWidth="2" strokeDasharray="4,4" />
              <path d="M 380 0 L 380 35 L 490 35 L 490 80" stroke="black" strokeWidth="2" strokeDasharray="4,4" />
              <path d="M 380 0 L 380 35 L 600 35 L 600 80" stroke="black" strokeWidth="2" strokeDasharray="4,4" />
              <path d="M 380 0 L 380 35 L 710 35 L 710 80" stroke="black" strokeWidth="2" strokeDasharray="4,4" />
            </svg>
          </div>

          {/* MOBILE DOWN ARROW BETWEEN CARD AND BOTTOM ROW */}
          <div className="md:hidden my-6 text-xl font-mono animate-pulse">↓</div>

          {/* BOTTOM ROW: ACTIONS OUT (Slack, Telegram, Discord, Email, Linear, Jira, More) */}
          <div className="grid grid-cols-4 md:grid-cols-7 gap-4 w-full relative z-10 mt-6 px-2">
            {/* Slack */}
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 border-2 border-black bg-white flex items-center justify-center shadow-[4px_4px_0_0_#000]">
                <img src="/integrations/slack.png" alt="Slack" className="w-8 h-8 object-contain" />
              </div>
              <span className="text-[9px] font-black uppercase mt-2 text-zinc-500">SLACK</span>
            </div>

            {/* Telegram */}
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 border-2 border-black bg-white flex items-center justify-center shadow-[4px_4px_0_0_#000]">
                <svg className="w-8 h-8 text-[#229ED9]" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.2-.02-.08.02-1.3 1.02-3.66 2.61-.35.24-.66.36-.95.35-.32-.01-.93-.18-1.39-.33-.56-.18-1-.28-.96-.59.02-.16.24-.33.67-.5 2.62-1.14 4.36-1.89 5.24-2.26 2.49-.97 3.01-1.14 3.35-1.14.07 0 .24.02.35.1.09.07.12.16.13.22.01.07.01.15 0 .22z" />
                </svg>
              </div>
              <span className="text-[9px] font-black uppercase mt-2 text-zinc-500">TELEGRAM</span>
            </div>

            {/* Discord */}
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 border-2 border-black bg-white flex items-center justify-center shadow-[4px_4px_0_0_#000]">
                <svg className="w-8 h-8 text-[#5865F2]" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0c-.163-.385-.397-.874-.608-1.25a.077.077 0 00-.078-.037 19.736 19.736 0 00-4.885 1.515.07.07 0 00-.032.028C.533 9.046-.32 13.58.099 18.058a.082.082 0 00.031.056 19.908 19.908 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 01.077-.011c3.928 1.793 8.18 1.793 12.061 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.3 12.3 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.156-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                </svg>
              </div>
              <span className="text-[9px] font-black uppercase mt-2 text-zinc-500">DISCORD</span>
            </div>

            {/* Email */}
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 border-2 border-black bg-white flex items-center justify-center shadow-[4px_4px_0_0_#000]">
                <svg className="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <span className="text-[9px] font-black uppercase mt-2 text-zinc-500">EMAIL</span>
            </div>

            {/* Linear */}
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 border-2 border-black bg-white flex items-center justify-center shadow-[4px_4px_0_0_#000]">
                <img src="/integrations/linear.png" alt="Linear" className="w-8 h-8 object-contain" />
              </div>
              <span className="text-[9px] font-black uppercase mt-2 text-zinc-500">LINEAR</span>
            </div>

            {/* Jira */}
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 border-2 border-black bg-white flex items-center justify-center shadow-[4px_4px_0_0_#000]">
                <img src="/integrations/jira.png" alt="Jira" className="w-8 h-8 object-contain" />
              </div>
              <span className="text-[9px] font-black uppercase mt-2 text-zinc-500">JIRA</span>
            </div>

            {/* More Actions */}
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 border-2 border-dashed border-zinc-400 bg-white flex items-center justify-center shadow-[4px_4px_0_0_#ccc]">
                <span className="text-[10px] font-black text-zinc-400 uppercase">+ MORE</span>
              </div>
              <span className="text-[9px] font-black uppercase mt-2 text-zinc-400">AND MORE</span>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
